import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { coaching, type PlaceCandidate } from '../lib/api';
import type { BirthDraft } from '../lib/birth';
import { CoachButton, Field, inputClass } from './primitives';

/** Birth date, time (or "unknown") and a server-side place search. */
export function BirthDetailsFields({
  value,
  onChange,
}: {
  value: BirthDraft;
  onChange: (next: BirthDraft) => void;
}) {
  const [query, setQuery] = useState('');
  const search = useMutation({
    mutationFn: (q: string) => coaching.geocode(q),
    meta: { errorMessage: 'Place search is unavailable right now. Try again shortly.' },
  });
  const choose = (place: PlaceCandidate) => {
    onChange({ ...value, birthPlace: place.displayName, birthLat: place.lat, birthLng: place.lng });
    search.reset();
    setQuery('');
  };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-5">
      <div className="space-y-5 lg:grid lg:grid-cols-2 lg:gap-5 lg:space-y-0">
        <Field label="Birth date" htmlFor="birth-date">
          <input
            id="birth-date"
            type="date"
            min="1900-01-01"
            max={today}
            className={inputClass}
            value={value.birthDate}
            onChange={(e) => onChange({ ...value, birthDate: e.target.value })}
          />
        </Field>
        <Field label="Birth time" htmlFor="birth-time" hint="Local time where you were born.">
          <input
            id="birth-time"
            type="time"
            className={inputClass}
            disabled={!value.birthTimeKnown}
            value={value.birthTimeKnown ? value.birthTime : ''}
            onChange={(e) => onChange({ ...value, birthTime: e.target.value })}
          />
          <label className="mt-2 flex items-center gap-2 text-sm text-ink/80">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[#5F6A62]"
              checked={!value.birthTimeKnown}
              onChange={(e) => onChange({ ...value, birthTimeKnown: !e.target.checked })}
            />
            I don't know my birth time
          </label>
        </Field>
      </div>
      <Field
        label="Birth place"
        htmlFor="birth-place"
        hint={
          value.birthPlace
            ? `Selected: ${value.birthPlace}`
            : 'Search a city, then choose it from the list.'
        }
      >
        <div className="flex gap-2">
          <input
            id="birth-place"
            className={inputClass}
            placeholder="e.g. Quezon City"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim().length >= 2) {
                e.preventDefault();
                search.mutate(query.trim());
              }
            }}
          />
          <CoachButton
            disabled={query.trim().length < 2 || search.isPending}
            onClick={() => search.mutate(query.trim())}
          >
            {search.isPending ? 'Searching' : 'Search'}
          </CoachButton>
        </div>
        {search.data && (
          <ul className="mt-2 border border-border">
            {search.data.length === 0 && (
              <li className="px-3.5 py-3 text-sm text-muted-foreground">
                No places found. Try a nearby city.
              </li>
            )}
            {search.data.map((place) => (
              <li key={`${place.lat},${place.lng}`}>
                <button
                  type="button"
                  onClick={() => choose(place)}
                  className="block w-full border-b border-border px-3.5 py-3 text-left text-sm text-ink last:border-b-0 hover:bg-surface"
                >
                  {place.displayName}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Field>
    </div>
  );
}
